// Phase 5 (web revival) — TanStack Router setup.
//
// Code-defined route tree (not file-based) because we have ~7 pages —
// the file-based generator adds tooling complexity without earning its
// keep. The auth gate is a single `beforeLoad` on the protected layout
// that redirects unauthenticated callers to /login.

import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { isAuthenticated } from './auth';
import { AppLayout } from '../components/Layout';
import { LoginPage } from '../pages/Login';
import { LibraryPage } from '../pages/Library';
import { SharedWithMePage } from '../pages/SharedWithMe';
import { AlbumsPage } from '../pages/Albums';
import { AlbumDetailPage } from '../pages/AlbumDetail';
import { AssetDetailPage } from '../pages/AssetDetail';
import { EditPage } from '../pages/Edit';
import { SettingsPage } from '../pages/Settings';

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: () => {
    // Already signed in? Skip the form.
    if (isAuthenticated()) {
      throw redirect({ to: '/library' });
    }
  },
});

const authedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  component: AppLayout,
  beforeLoad: ({ location }) => {
    if (!isAuthenticated()) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
  },
});

const indexRedirect = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/library' });
  },
  component: () => null,
});

const libraryRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/library',
  component: LibraryPage,
});

const sharedWithMeRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/shared-with-me',
  component: SharedWithMePage,
});

const albumsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/albums',
  component: AlbumsPage,
});

const albumDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/album/$albumID',
  component: () => {
    const { albumID } = albumDetailRoute.useParams();
    return <AlbumDetailPage albumID={albumID} />;
  },
});

const assetDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/asset/$assetID',
  component: () => {
    const { assetID } = assetDetailRoute.useParams();
    return <AssetDetailPage assetID={assetID} />;
  },
});

const editRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/asset/$assetID/edit',
  component: () => {
    const { assetID } = editRoute.useParams();
    return <EditPage assetID={assetID} />;
  },
});

const settingsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/settings',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  authedLayoutRoute.addChildren([
    indexRedirect,
    libraryRoute,
    sharedWithMeRoute,
    albumsRoute,
    albumDetailRoute,
    assetDetailRoute,
    editRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
